<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import { requirejs } from '~/modules/require';
import { useTranslations } from '~/composables/translations';
import MapViewer from '~/components/common/MapViewer.vue';
import ClickableIcon from '~/components/common/ClickableIcon.vue';
import Footer from '~/components/Footer.vue';
import LabCard from '~/components/lab/LabCard.vue';
import LabCardSkeleton from '~/components/lab/LabCardSkeleton.vue';
import { useMeStore } from '~/stores/me';
import { useCountryStore } from '~/stores/countries';
import { useCartStore } from '~/stores/cart';
import { useLabExplorationStore } from '~/stores/lab-exploration';

import vuBtn from '@ds/vuekit/src/components/vu-btn.vue';
import vuMessage from '@ds/vuekit/src/components/vu-message/vu-message.vue';
import vuMultipleSelect from '@ds/vuekit/src/components/vu-multiple-select.vue';
import vuSelect from '@ds/vuekit/src/components/vu-select.vue';
import vuInput from '@ds/vuekit/src/components/vu-input.vue';

import { useLabStore } from '~/stores/lab';
import { storeToRefs } from 'pinia';
import { useTracker } from '~/composables/tracker';
import { useSettingsStore } from '~/stores/settings';

// I18n
const { $i18n } = useTranslations('ordering');

// Constants
const TOP_MARGIN = 15;
const BOTTOM_MARGIN = 25;
const SIDE_MARGIN = 10;
let RATIO_PIXEL_COORD = 0;

// Router and Traker
const router = useRouter();
const tracker = useTracker();

// Store
const me = useMeStore();
const countryStore = useCountryStore();
const lab = useLabStore();
const settings = useSettingsStore();
const labExploration = useLabExplorationStore();
const cart = useCartStore();

// Refs
const map = ref();
const selected = ref([]);
const mapMode = ref(false);
const mapReady = ref(false);
const mapIsInit = ref(false);
const bounds = ref(null);
const expandFilters = ref(false);
const cacheManager = ref(null);
const isLoadingRef = ref(true);
const labs = ref([]);
const empMode = ref(false);
const companyAllowPublicSellers = ref(false);
const { countryFilter } = storeToRefs(lab);
const { certificationFilter } = storeToRefs(lab);
const countryFilterEnable = ref(false);
const certificationFilterEnable = ref(false);
const nameFilterEnable = ref(false);
const nameFilter = ref();

// Data properties
const labsContainerClass = ref(
  'flex-grow-1 grid grid-1-columns--xs grid-2-columns--sm grid-3-columns grid-gap-5px lab-list-pro-view',
);
const colors = [
  '#078BFA',
  '#F95753',
  '#6FBC4B',
  '#9E846A',
  '#AA4BB2',
  '#1A997B',
  '#F564A3',
  '#FF8A2E',
];

// Computed properties
const sortFilter = ref('');

const filteredLabs = computed(() => {
  let labList = labs.value;
  if (countryFilter.value && countryFilter.value.length) {
    const allowedCountries = countryFilter.value.map((x) => x.value);
    labList = labList.filter((el) => allowedCountries.includes(el.country));
  }

  if (certificationFilter.value && certificationFilter.value.length) {
    const allowedCerts = certificationFilter.value.map((x) => x.value);
    const hasAllowedCert = (lab: (typeof labList)[number]) =>
      lab.certifications.some((cert) => allowedCerts.includes(cert));

    labList = labList.filter(hasAllowedCert);
  }

  if (nameFilter.value && nameFilter.value.length >= 2) {
    labList = labList.filter((el) =>
      el.shopName.toLowerCase().includes(nameFilter.value.toLowerCase()),
    );
  }

  if (mapMode.value && mapReady.value && bounds.value) {
    labList = labList.filter(filterVisibleOnMap);
  }

  return labList;
});

const labsCount = computed(() => filteredLabs.value.length);

const exploreMode = computed(() => router.currentRoute.value.name === 'LabsExploration');

const displayEmpBanner = computed(() => empMode.value && companyAllowPublicSellers.value);

const selectedLabs = computed(() => labs.value.filter((el) => selected.value.includes(el.id)));

const sortOptions = computed(() => {
  const options = [{ value: 'name', label: $i18n('name') }];

  // Check if any lab has valid pricing data
  const hasValidPrice = filteredLabs.value.some((lab) => lab.totalPrice && lab.totalPrice !== '-');

  // Check if any lab has valid TAT data
  const hasValidTat = filteredLabs.value.some(
    (lab) => lab.offers && lab.offers.some((offer) => offer.tat),
  );

  if (hasValidTat) {
    options.push({ value: 'tat', label: $i18n('providers_turnAroundTime') });
  }

  if (hasValidPrice) {
    options.push({ value: 'price', label: $i18n('providers_price') });
  }

  return options;
});

const availableCountryOptions = computed(() => {
  // Count labs by country
  const countryCount = labs.value.reduce(
    (acc, lab) => {
      if (lab.country) {
        acc[lab.country] = (acc[lab.country] || 0) + 1;
      }

      return acc;
    },
    {} as Record<string, number>,
  );

  // Get unique countries from labs and create options
  const countriesOptions: any = Object.keys(countryCount)
    .map((isocode: string) => {
      const country = countryStore.getCountryByIsoCode(isocode);
      if (!country) {
        return null;
      }

      return {
        label: `${country.name} (${countryCount[isocode]})`,
        value: country.isocode3,
        group:
          countryStore.estimatedUserLocation === country.isocode3 ||
          countryStore.estimatedUserLocation === country.isocode2
            ? 'your_country'
            : 'others_countries',
      };
    })
    .filter(Boolean) // Remove null entries
    .sort((a: any, b: any) => a.label.localeCompare(b.label));

  // Move user country at the top of the list
  const index: number = countriesOptions.findIndex((el: any) => el.group === 'your_country');
  const element = countriesOptions.at(index);

  if (index !== -1) {
    countriesOptions.splice(index, 1);
    countriesOptions.unshift(element);
  }

  return countriesOptions;
});

// Watch for changes
watch(filteredLabs, () => {
  const stillOkLabs = [];
  selected.value.forEach((selectedLab) => {
    if (filteredLabs.value.find((lab) => lab.id === selectedLab.id || lab.id === selectedLab)) {
      stillOkLabs.push(selectedLab);
    }
  });

  if (selected.value.length !== stillOkLabs.length) {
    showMessage({ text: $i18n('providers_labsDeleted'), timeout: 5000 });
  }

  selected.value = stillOkLabs;
});

watch(sortFilter, async () => {
  labExploration.setSortFilter(sortFilter.value);
  await getLabs();
});

// Methods
const getLabs = async () => {
  const labsList = labExploration.sortedLabs.slice(0);

  labs.value = labsList;
  return labsList;
};

const allowPublic = async () => labExploration.allowPublic;

const showMessage = (message: any) => {
  // Implementation might depend on your message service
  // For example: this.$message in the original code
  console.log('Message:', message);
};

const toggleSelected = async (labId) => {
  // Check if we are in explore mode
  if (exploreMode.value) {
    const { shopName } = labExploration.getLab(labId);
    await tracker?.event('lab_card_provider_clicked', 'navigation', 'CLICK', [
      '',
      '',
      '',
      '',
      '',
      shopName,
      labId,
    ]);
    cart.setExploreMode(true);
    await saveSelectedLabs([labId]);
    await router.push({ name: 'QuoteUploadPart' });
    return;
  }

  // Check if we need to remove
  if (selected.value.includes(labId)) {
    tracker?.event('DeselectALab', 'Click on button', 'Edit');
    selected.value.splice(selected.value.indexOf(labId), 1);
    return;
  }

  // Then it's for adding a lab
  // Check if already 3
  if (selected.value && selected.value.length >= 3) {
    showMessage({
      text: $i18n('providers_noMoreThanThreeProviders'),
      color: 'error',
      timeout: 5000,
    });
    return;
  }

  await checkLabMembers(labId);
  tracker?.event('SelectALab', 'Click on button', 'Edit');
  selected.value.push(labId);
};

const initialsAndColor = (lab) => {
  const nameSplit = lab.shopName.split(' ');
  let initials = nameSplit[0].charAt(0).toUpperCase();
  let colorIndex = 0;

  if (nameSplit[1]) {
    initials += nameSplit[1].charAt(0).toUpperCase();
    colorIndex = (initials.charCodeAt(0) + initials.charCodeAt(1)) % colors.length;
  } else {
    colorIndex = initials.charCodeAt(0) % colors.length;
  }

  return { initials, color: colors[colorIndex] };
};

const backToFirstStep = async () => {
  labExploration.resetFilters(labExploration);
  await labExploration.waitGetShopsV10();

  await saveSelectedLabs([]);
  await router.push({ name: 'QuoteEdition' });
};

const backToSecondStep = async () => {
  labExploration.resetFilters(labExploration);
  await labExploration.waitGetShopsV10();
  await saveSelectedLabs(selected.value);
  await router.push({ name: 'QuoteEdition' });
};

const buildMapView = () => {
  setTimeout(() => {
    // For asynchronous map loading
    let lastMapPosition;

    if (!mapIsInit.value) {
      lastMapPosition = cacheManager.value.get('MP3DP_LAST_MAP_POSITION', Object) || {};
      if (!Array.isArray(lastMapPosition.position)) {
        lastMapPosition.position = [28, 0];
      }

      if (Number.isNaN(lastMapPosition.zoom)) {
        lastMapPosition.zoom = 2;
      }

      mapIsInit.value = true;

      if (!mapReady.value) {
        map.value.loadMap();
        // Map.value.map.setView(lastMapPosition.position, lastMapPosition.zoom);
        mapReady.value = true;
      }
    }
  }, 0);
};

const displayMap = async () => {
  mapMode.value = !mapMode.value;
  labExploration.setMapMode(mapMode.value);
  if (mapMode.value) {
    setTimeout(() => {
      map.value.updateMap();
      mapReady.value = true;
    }, 410);
  } else {
    mapReady.value = false;
  }
};

const centerHasChanged = (iData) => {
  if (iData.target) {
    const center = iData.target.getCenter();

    cacheManager.value.set('MP3DP_LAST_MAP_POSITION', {
      position: [center.lat, center.lng],
      zoom: iData.target.getZoom(),
    });

    bounds.value = {
      ne: iData.target.getBounds().getNorthEast(),
      sw: iData.target.getBounds().getSouthWest(),
    };

    // Store new map's bounds in localStorage
    cacheManager.value.set('MP3DP_LAST_MAP_BOUNDS', bounds.value);

    // Update margin to display (or not) lab on map limit
    const containerH = iData.target.getSize().y; // Pixel
    const mapH = bounds.value.ne.lat - bounds.value.sw.lat; // Coord

    RATIO_PIXEL_COORD = mapH / containerH;
  }
};

const onMapLoaded = (e) => {
  centerHasChanged(e);
};

const filterVisibleOnMap = (lab) => {
  if (bounds.value && RATIO_PIXEL_COORD) {
    if (lab.longitude && lab.latitude) {
      if (lab.latitude < bounds.value.sw.lat - BOTTOM_MARGIN * RATIO_PIXEL_COORD) {
        return false;
      }

      if (lab.latitude > bounds.value.ne.lat - TOP_MARGIN * RATIO_PIXEL_COORD) {
        return false;
      }

      if (lab.longitude < bounds.value.sw.lng - SIDE_MARGIN * RATIO_PIXEL_COORD) {
        return false;
      }

      if (lab.longitude > bounds.value.ne.lng + SIDE_MARGIN * RATIO_PIXEL_COORD) {
        return false;
      }

      return true;
    }
  }

  return false;
};

const onTopBarSearch = async (iSearch: string) => {
  labExploration.setSearchFilter(iSearch.toLowerCase());
};

const onTopBarClear = async () => {
  labExploration.setSearchFilter('');
};

const next = async () => {
  saveSelectedLabs(selected.value);
  await cart.waitLabsAreLoaded();
  await saveCart();
  cart.setPurchaseMode('multibidding');
  await router.push({ name: 'QuoteSummaryShipping' });
};

const saveCart = async (patchCart?: any) => {
  try {
    await cart.patchCart({ patchCart });
  } finally {
    isLoadingRef.value = false;
  }
};

const togglePublicLabsVisibility = async () => {
  labExploration.setAllowPublic(!allowPublic);

  if (!allowPublic) {
    const publicLabs = selected.value.filter((el) => el.isIncludedInPMPFilter !== 'true');
    if (publicLabs.length) {
      showMessage({ text: $i18n('providers_labsDeleted'), timeout: 5000, color: 'primary' });
      selected.value = selected.value.filter((el) => el.isIncludedInPMPFilter === 'true');
    }
  }
};

const saveSelectedLabs = async (labs: any[]) => {
  cart.setSelectedLabs(labs.map((id: string) => ({ id })));
};

const checkLabMembers = async (id: string) => {
  try {
    await cart.checkLabsMembers({ ids: [id], esid: router.currentRoute.value.params?.esid });
  } catch {
    showMessage({ text: $i18n('summary_errorSendToOwnLab'), color: 'error', timeout: 5000 });
    throw new Error("Can't send request to own lab");
  }
};

const goToMoreInfo = (labStoreUrl: string) => {
  window.open(router.resolve({ name: 'Showroom', params: { id: labStoreUrl } }).href);
};

const handleLogoError = (lab) => {
  lab.logoUrl = ''; // You can mutate it here in the parent
};

const resetFilters = () => {
  nameFilter.value = null;
  countryFilter.value = [];
  certificationFilter.value = [];
};

// Lifecycle hooks

onMounted(async () => {
  isLoadingRef.value = true;

  if (!labs.value?.length) {
    if (!labExploration.sortedLabs?.length) {
      await labExploration.fetchShopsAndCount();
    }

    await getLabs();
  }

  if (!countryStore.$state.countries.length) {
    await countryStore.fetchCountries();
  }

  empMode.value = labExploration.empMode;
  companyAllowPublicSellers.value = me.companyAllowPublicSellers;
  window.dispatchEvent(new Event('resize'));
  sortFilter.value = exploreMode.value ? 'name' : 'price';
  labExploration.setEmpMode(Boolean(router.currentRoute.value.params?.esid));

  isLoadingRef.value = false;

  if (localStorage.getItem('vue-cart-id') && !cart.cart.id && !exploreMode.value) {
    await cart.fetch(String(localStorage.getItem('vue-cart-id')));
    if (selected.value.length) {
      await cart.waitLabsAreLoaded();
    }
  }

  const [MPCacheManager] = await requirejs<[any]>(['DS/MP3DPServices/MPCacheManager']);
  cacheManager.value = new MPCacheManager();

  buildMapView();
  selected.value = cart.selectedLabs.map((el) => el.id);

  const [PlatformAPI] = await requirejs<[any]>(['DS/PlatformAPI/PlatformAPI']);
  PlatformAPI.subscribe('SearchBar/search', onTopBarSearch);
  PlatformAPI.subscribe('SearchBar/clear', onTopBarClear);

  // Initial setup
  labExploration.setExploreMode(exploreMode.value);

  if (exploreMode.value) {
    await cart.resetCart();
  }

  await me.fetchMe();
  await me.fetchMeShops();
  await labExploration.fetchShopsAndCount();
});

onBeforeUnmount(async () => {
  // Remove explore mode if there is no selected labs
  if (cart.selectedLabs.length !== 1) {
    labExploration.setExploreMode(false);
  }

  const [PlatformAPI] = await requirejs<[any]>(['DS/PlatformAPI/PlatformAPI']);
  // Unsubscribe from platform API events
  PlatformAPI.unsubscribe('SearchBar/search', onTopBarSearch);
  PlatformAPI.unsubscribe('SearchBar/clear', onTopBarClear);

  resetFilters();
});
</script>

<template>
  <div>
    <div :class="['quote-providers mp-fill-height', { 'map-active': mapMode }]">
      <div
        :style="mapMode ? '' : 'height: 100%; overflow-y: auto'"
        :class="{ 'custom-scroll-bar-right--no-margin': !mapMode }"
      >
        <div class="flex body">
          <!-- LEFT PANEL -->
          <div v-if="!settings.isMobileDevice" class="left-panel">
            <div class="sticky-container mp-container">
              <div class="select-providers">
                <div class="select-title blue-4">
                  {{ $i18n('labSelector_filters') }}
                </div>
              </div>
              <div class="filter-row" @click="nameFilterEnable = !nameFilterEnable">
                <div class="flex justify-space-between">
                  <div class="section-title">
                    {{ $i18n('filters_name') }}
                  </div>
                  <span
                    :class="[
                      'fonticon',
                      nameFilterEnable ? 'fonticon-chevron-up' : 'fonticon-chevron-down',
                    ]"
                  />
                </div>
              </div>
              <div v-if="nameFilterEnable" class="filter-row">
                <vu-input v-model="nameFilter" :placeholder="$i18n('filters_name_placeholder')" />
              </div>
              <div class="filter-row" @click="countryFilterEnable = !countryFilterEnable">
                <div class="flex justify-space-between">
                  <div class="section-title">
                    {{ $i18n('filters_countries') }}
                  </div>
                  <span
                    :class="[
                      'fonticon',
                      countryFilterEnable ? 'fonticon-chevron-up' : 'fonticon-chevron-down',
                    ]"
                  />
                </div>
              </div>
              <div v-if="countryFilterEnable" class="filter-row">
                <vu-multiple-select
                  v-if="!isLoadingRef"
                  v-model="countryFilter"
                  v-mask="!countryStore.$state.countries.length"
                  :class="[
                    'sort-select country-select',
                    {
                      'has-current-user-country-option':
                        availableCountryOptions.length > 1 &&
                        availableCountryOptions.filter(
                          (el: { group: string }) => el.group === 'your_country',
                        ).length > 0,
                    },
                  ]"
                  :placeholder="$i18n('providers_selectCountry')"
                  :options="availableCountryOptions"
                />
              </div>
              <div
                class="filter-row"
                @click="certificationFilterEnable = !certificationFilterEnable"
              >
                <div class="flex justify-space-between">
                  <div class="section-title">
                    {{ $i18n('filters_certifications') }}
                  </div>
                  <span
                    :class="[
                      'fonticon',
                      certificationFilterEnable ? 'fonticon-chevron-up' : 'fonticon-chevron-down',
                    ]"
                  />
                </div>
              </div>
              <div v-if="certificationFilterEnable" class="filter-row">
                <vu-multiple-select
                  v-model="certificationFilter"
                  v-if="!isLoadingRef"
                  class="sort-select certification-select"
                  :placeholder="$i18n('providers_selectCertification')"
                  :options="[
                    {
                      label: $i18n('providers_iso9001'),
                      value: 'ISO 9001:2008',
                    },
                    {
                      label: $i18n('providers_iso90012015'),
                      value: 'ISO 9001:2015',
                    },
                    {
                      label: $i18n('providers_en9100'),
                      value: 'AS/EN/JISQ 9100:2009 (Rev C)',
                    },
                    {
                      label: $i18n('providers_en91002016'),
                      value: 'AS/EN/JISQ 9100:2016 (Rev D)',
                    },
                    {
                      label: $i18n('providers_easaPart'),
                      value: 'EASA Part 21G',
                    },
                    {
                      label: $i18n('providers_iso13485'),
                      value: 'ISO 13485:2003',
                    },
                    {
                      label: $i18n('providers_iso134852016'),
                      value: 'ISO 13485:2016',
                    },
                    {
                      label: $i18n('providers_nadcapHT'),
                      value: 'NADCAP Heat Treating',
                    },
                    {
                      label: $i18n('providers_nadcapC'),
                      value: 'NADCAP Coating',
                    },
                    {
                      label: $i18n('providers_nadcapMI'),
                      value: 'NADCAP Measurement & Inspection',
                    },
                    {
                      label: $i18n('providers_nadcapNT'),
                      value: 'NADCAP Nondestructive Testing',
                    },
                  ]"
                />
              </div>
              <div class="mt-10px flex justify-content-center">
                <vu-btn color="primary" @click="resetFilters">
                  {{ $i18n('filters_reset') }}
                </vu-btn>
              </div>
              <div v-if="!exploreMode && sortOptions.length > 1" class="mt-40px">
                <div class="select-providers">
                  <div class="select-title blue-4">
                    {{ $i18n('providers_sortBy') }}
                  </div>
                </div>
                <vu-select
                  v-model="sortFilter"
                  :placeholder="$i18n('providers_selectSortOption')"
                  :options="sortOptions"
                />
              </div>
            </div>
          </div>
          <!-- TOP PANEL -->
          <div :class="settings.isMobileDevice ? 'top-panel-mobile' : 'top-panel'">
            <div class="sticky-container mp-container">
              <div :class="['select-providers', { 'filters-active': expandFilters }]">
                <div class="select-title blue-4">
                  <span class="fonticon fonticon-select-on" />{{
                    exploreMode ? $i18n('providers_selectProvider') : $i18n('providers_selectUpTo3')
                  }}
                  <span
                    v-if="!exploreMode"
                    class="white-cross fonticon fonticon-close"
                    @click="backToFirstStep"
                  />
                </div>
                <div v-if="!exploreMode" class="selected-labs-line row">
                  <div class="col-md-9 col-xs-12 labs-boxes no-padding">
                    <div
                      v-for="selectedLab in selectedLabs"
                      :key="selectedLab.id"
                      class="border-thin grey-0 grey-4--border selected-lab-part selected-lab"
                      :name="selectedLab.shopName.toLowerCase()"
                    >
                      <div class="icon-container">
                        <img
                          v-if="selectedLab.logoUrl"
                          class="img-circle img-responsive"
                          :src="selectedLab.logoUrl"
                          @error="selectedLab.logoUrl = ''"
                        />
                        <div
                          v-else
                          class="logo-initials img-circle text-center"
                          :style="{ 'background-color': initialsAndColor(selectedLab).color }"
                        >
                          {{ initialsAndColor(selectedLab).initials }}
                        </div>
                      </div>
                      <span class="shop-name">{{ selectedLab.shopName }}</span>
                      <ClickableIcon
                        icon="fonticon-close"
                        @click="selected.splice(selected.indexOf(selectedLab.id), 1)"
                      />
                    </div>
                    <div v-if="!selected[2]" class="selected-lab-part selected-lab-placeholder">
                      {{ $i18n('providers_selectProvider') }}
                    </div>
                  </div>
                  <div
                    class="buttons-zone col-md-3 col-xs-12 no-padding grid grid-2-columns grid-gap-5px"
                  >
                    <vu-btn
                      block
                      class="no-margin back-next-button back-btn btn-with-icon"
                      @click="backToSecondStep"
                    >
                      <span class="fonticon fonticon-chevron-left" />
                      {{ $i18n('providers_back') }}
                    </vu-btn>
                    <vu-btn
                      block
                      class="no-margin back-next-button next-btn btn-with-icon"
                      color="primary"
                      :disabled="!selected.length"
                      @click="next"
                    >
                      {{ $i18n('providers_next') }}
                      <span class="fonticon fonticon-chevron-right right" />
                    </vu-btn>
                  </div>
                </div>
                <div class="results-map-sort flex justify-space-between">
                  <div>
                    <b class="lab-count">{{ labsCount }}</b>
                    {{ labsCount > 1 ? $i18n('providers_providers') : $i18n('providers_provider') }}
                  </div>
                  <b v-if="labsCount < 1">{{ $i18n('providers_no_match') }}</b>
                  <div class="map-icon">
                    <span
                      :class="['fonticon', 'fonticon-map', { active: mapMode }]"
                      @click="displayMap"
                    />
                  </div>
                  <div
                    v-if="settings.isMobileDevice"
                    :class="['expand-sort', { 'filters-collapsed': !expandFilters }]"
                    @click="expandFilters = !expandFilters"
                  >
                    <span class="fonticon fonticon-down-dir" /> {{ $i18n('providers_sortResults') }}
                  </div>
                </div>
              </div>
              <div v-show="expandFilters && settings.isMobileDevice" class="filters-line">
                <div
                  :class="[
                    'filters-container grid grid-1-columns--xs grid-gap-5px',
                    { 'grid-2-columns': exploreMode, 'grid-3-columns': !exploreMode },
                  ]"
                >
                  <div v-if="!exploreMode">
                    <span class="filter-label">{{ $i18n('providers_sortBy') }}</span>
                    <vu-select
                      v-model="sortFilter"
                      class="flex"
                      :placeholder="$i18n('providers_selectSortOption')"
                      :options="[
                        { value: 'name', label: $i18n('name') },
                        { value: 'tat', label: $i18n('providers_turnAroundTime') },
                        { value: 'price', label: $i18n('providers_price') },
                      ]"
                    />
                  </div>
                  <div class="group-filter-and-label">
                    <span class="filter-label">{{ $i18n('providers_filterBy') }}</span>
                    <vu-input
                      v-model="nameFilter"
                      :placeholder="$i18n('filters_name_placeholder')"
                    />
                  </div>
                  <vu-multiple-select
                    v-if="!isLoadingRef"
                    v-model="countryFilter"
                    v-mask="!countryStore.$state.countries.length"
                    class="sort-select country-select flex"
                    :placeholder="$i18n('providers_selectCountry')"
                    :options="availableCountryOptions"
                  />
                  <vu-multiple-select
                    v-model="certificationFilter"
                    v-if="!isLoadingRef"
                    class="sort-select certification-select"
                    :placeholder="$i18n('providers_selectCertification')"
                    :options="[
                      {
                        label: $i18n('providers_iso9001'),
                        value: 'ISO 9001:2008',
                      },
                      {
                        label: $i18n('providers_iso90012015'),
                        value: 'ISO 9001:2015',
                      },
                      {
                        label: $i18n('providers_en9100'),
                        value: 'AS/EN/JISQ 9100:2009 (Rev C)',
                      },
                      {
                        label: $i18n('providers_en91002016'),
                        value: 'AS/EN/JISQ 9100:2016 (Rev D)',
                      },
                      {
                        label: $i18n('providers_easaPart'),
                        value: 'EASA Part 21G',
                      },
                      {
                        label: $i18n('providers_iso13485'),
                        value: 'ISO 13485:2003',
                      },
                      {
                        label: $i18n('providers_iso134852016'),
                        value: 'ISO 13485:2016',
                      },
                      {
                        label: $i18n('providers_nadcapHT'),
                        value: 'NADCAP Heat Treating',
                      },
                      {
                        label: $i18n('providers_nadcapC'),
                        value: 'NADCAP Coating',
                      },
                      {
                        label: $i18n('providers_nadcapMI'),
                        value: 'NADCAP Measurement & Inspection',
                      },
                      {
                        label: $i18n('providers_nadcapNT'),
                        value: 'NADCAP Nondestructive Testing',
                      },
                    ]"
                  />
                </div>
              </div>
              <vu-message
                class="emp-lab-list-message"
                :show="displayEmpBanner"
                :closable="false"
                color="primary"
              >
                <span v-if="!allowPublic"
                  >{{ labsCount }}
                  {{
                    labsCount > 1
                      ? $i18n('providers_availableServiceProviders')
                      : $i18n('providers_availableServiceProvider')
                  }}</span
                >
                <a style="cursor: pointer" @click="togglePublicLabsVisibility">{{
                  !allowPublic
                    ? $i18n('providers_viewAllProviders')
                    : $i18n('providers_viewEcosysProviders')
                }}</a>
              </vu-message>
            </div>
            <!-- LAB LISTS -->
            <div
              :class="[
                'labs-list flex mp-container',
                { 'filters-active': expandFilters, 'map-active': mapMode },
              ]"
            >
              <div
                :class="[
                  'list-and-map-container flex flex-grow-1',
                  { 'map-open flex-column-reverse': mapMode, 'filters-active': expandFilters },
                ]"
              >
                <div
                  class="flex flex-grow-1 list-container transition-width custom-scroll-bar-right"
                >
                  <div :class="labsContainerClass">
                    <template v-if="!isLoadingRef">
                      <template v-if="labs.length > 0">
                        <div
                          v-for="filteredLab in filteredLabs"
                          :key="'labcard-' + filteredLab.id"
                          class="fade-keyframe"
                          :name="filteredLab.shopName.toLowerCase()"
                        >
                          <LabCard
                            :lab="filteredLab"
                            :selected="selected.includes(filteredLab.id)"
                            :country="
                              countryStore.$state.countries.find((el) => {
                                return el.isocode3 === filteredLab.country;
                              })
                            "
                            :explore-mode="exploreMode"
                            @toggle-selected="toggleSelected(filteredLab.id)"
                            @more-info="goToMoreInfo"
                            @logo-error="handleLogoError"
                          />
                        </div>
                      </template>
                      <p v-else class="text-center mt-20px" style="grid-column-start: 2">
                        {{ $i18n('noShippingInYourCountry') }}
                      </p>
                    </template>
                    <template v-else>
                      <LabCardSkeleton
                        v-for="i in 24"
                        :key="i"
                        class="fade-keyframe"
                        :is-direct-purchase="false"
                      />
                    </template>
                  </div>
                </div>
                <MapViewer
                  ref="map"
                  :center-has-changed="centerHasChanged"
                  :on-map-loaded="onMapLoaded"
                  :explore="exploreMode"
                  :labs-list="filteredLabs"
                  :selected-labs="selected"
                  @toggle-selected="toggleSelected"
                />
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@use '/assets/scss/variables' as variables;

.sort-select.country-select {
  &.has-current-user-country-option ul.vu-select-options:not(.brute).select-options--multiple li {
    border-bottom: 3px double variables.$grey-5;
  }

  &.has-current-user-country-option
    ul.vu-select-options:not(.brute).select-options--multiple
    li
    ~ li {
    border-bottom: 1px solid variables.$grey-1;
  }
}

.list-and-map-container {
  .labs-map-container {
    width: 0%;
    height: 100%;
    background-color: #dddddd;
    display: inline-block;
  }

  &:not(.map-open) .list-container {
    padding-bottom: 5px;
    align-items: flex-start;
  }

  &.map-open {
    width: 100%;
    overflow: hidden;
    // Remove the height of the map
    height: calc(100vh - 300px);

    &.filters-active {
      // Remove the height of the map and the filter area
      @media (max-width: variables.$screen-sm-max) {
        height: calc(100vh - 410px);
      }
    }

    .labs-map-container {
      width: 100%;
      height: 200px;
      margin-bottom: 5px;

      @media (min-width: variables.$screen-sm-max) {
        width: 70%;
        height: 100%;
        min-height: 500px;
        margin-bottom: 0;
      }
    }

    display: flex;

    .toggle-container {
      margin-right: 0;
    }

    .list-container {
      display: inline-block;
      max-height: calc(100vh - 221px);
      overflow: auto;
      margin-right: 4px;
      width: 100%;

      .list-header {
        .icon-container {
          .fonticon-th-list,
          .fonticon-th-list-large {
            transform: scale(0);
          }
        }
      }

      @media (min-width: variables.$screen-sm-max) {
        width: 30%;
      }

      @media (max-width: variables.$screen-sm-max) {
        max-height: calc(100% - 205px);
      }
    }

    .lab-list-pro-view {
      grid-template-columns: repeat(1, minmax(0, 1fr)) !important;

      .no-found-message {
        margin: auto;
        color: variables.$grey-5;
      }
    }

    .mp3dp-map {
      height: 100%;

      .on-hover {
        -webkit-filter: hue-rotate(180deg);
        filter: hue-rotate(180deg);
      }
    }

    .map-container {
      height: 100%;

      svg {
        height: auto;
        width: auto;
      }

      .leaflet-bar a,
      .leaflet-bar a:hover {
        text-decoration: none;
      }

      .locate-icon {
        font-size: 1.5em;
      }

      .leaflet-bottom,
      .leaflet-right,
      .leaflet-top,
      .leaflet-bottom {
        z-index: 2;
      }
    }

    .mp-map {
      width: 100%;
      height: 100%;
    }

    .leaflet-popup-content-wrapper {
      border-radius: 5px;

      .leaflet-popup-content {
        display: inline-block;
        margin: 3px 5px;
        padding: 10px 5px;
        width: 100%;
        min-width: 230px;
        height: 110px;

        .lab-title {
          text-transform: capitalize;
          font-size: 18px;
          font-weight: bold;
          margin: 0px;
          padding: 0px;
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
          color: variables.$grey-7;
        }

        .lab-card-container {
          width: 100%;
          padding: 5px 0 0 0;
          display: flex;
          flex-direction: row;
          border: none;
        }

        .lab-initial-container {
          flex-shrink: 0;
        }

        .lab-template-container {
          flex: 1 1 auto;
        }

        .price-div {
          height: 40px;
          line-height: 40px;
          padding: 0;
        }

        .price-div-extended {
          height: 50px;
          line-height: 50px;
          padding: 0;
        }

        .more-information-lab-select {
          line-height: 20px;
          font-size: 14px;
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
          display: block;
          padding: 0;
        }

        .stars-container {
          height: 20px;
          padding: 0px;
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
        }

        .starting {
          height: 10px;
          line-height: 10px;
          padding: 0;
          font-size: 10px;
          font-weight: bold;
          color: variables.$grey-7;
        }

        .more-information-lab-select {
          line-height: 20px;
          font-size: 14px;
        }

        .collection-search-lab-button-select {
          width: 100%;
          height: 35px;
          display: block;
        }

        .dot-container {
          padding: 0px 0px 2px;
        }

        .initial-dot-view {
          background-color: white !important;

          img {
            width: 70px !important;
            height: 70px !important;
          }
        }

        .logo-container {
          padding: 0;
        }

        .txt-container {
          padding: 0;
        }
      }
    }
  }

  @media (min-width: variables.$screen-sm-max) {
    flex-direction: row;
  }
}
</style>

<style lang="scss" scoped>
@use '/assets/scss/variables' as variables;

.sort-select.country-select {
  &.has-current-user-country-option ul.vu-select-options:not(.brute).select-options--multiple li {
    border-bottom: 3px solid variables.$grey-3;
  }

  &.has-current-user-country-option
    ul.vu-select-options:not(.brute).select-options--multiple
    li
    ~ li {
    border-bottom: 1px solid variables.$grey-1;
  }
}

.body {
  min-height: calc(100vh - 130px);
}

.left-panel {
  width: 20%;
  margin-left: 20px;

  .filter-row {
    border: 1px solid #d1d4d4;
    border-radius: 5px;
    padding: 10px;

    .section-title {
      font-weight: bold;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .autocomplete-searchbox input.minimized {
    width: 100% !important;
  }
}

.top-panel {
  width: 75%;
}

.top-panel-mobile {
  width: 100%;
}

.mp-container {
  max-width: 100%;
  margin: 0 auto;
  padding: 0px 10px;
  float: none !important;
}

.quote-providers {
  height: calc(100vh - 86px);
  width: 100vw;

  &:not(.map-active) {
    overflow: auto;
    margin: auto;
  }

  .labs-list {
    padding-top: 5px;
    margin: 0px auto;
    min-height: calc(100vh - 150px);
    margin-bottom: 40px;

    @media screen and (max-width: variables.$screen-sm-max) {
      min-height: calc(100vh - 203px);
    }

    @media screen and (max-width: variables.$screen-xs-max) {
      min-height: calc(100% - 218px);
    }

    &.masked .mask {
      background-color: transparent;
    }
  }

  .transition-width {
    transition: width 0.4s ease-in-out;
  }

  .img-circle {
    background-color: #ffffff;
  }

  .sticky-container {
    background-color: #f4f5f6;
    margin: 0px auto;
    z-index: 10;
    position: -webkit-sticky;
    position: sticky;
    top: 0px;
    margin: 25px auto 0px;

    .select-providers {
      border: 1px solid variables.$grey-4;
      border-radius: 5px;
      display: flex;
      flex-direction: column;
      background-color: #ffffff;
      text-align: center;

      @media (max-width: variables.$screen-xs-max) {
        margin: 0px auto;
      }

      &.filters-active {
        border-radius: 5px 5px 0px 0px;
      }

      .select-title {
        font-size: 16px;
        font-weight: bold;
        color: white;
        border-top-left-radius: 5px;
        border-top-right-radius: 5px;
        text-transform: uppercase;
        padding: 10px 15px 10px;

        .fonticon {
          margin-right: 15px;
        }

        .white-cross {
          float: right;
          cursor: pointer;
          opacity: 0.5;
          transform: translateX(16px);

          &:hover {
            opacity: 1;
          }
        }
      }

      .selected-labs-line {
        display: flex;
        flex-direction: row;
        margin: 15px 15px 5px;

        @media screen and (max-width: 991px) {
          display: block;
        }

        .labs-boxes {
          display: flex;
          flex-direction: row;

          @media screen and (max-width: 991px) {
            flex-direction: column;
          }

          .selected-lab-part {
            height: 38px;
            margin: 0 5px 0 0;
            flex: 1 1 0px;

            @media screen and (max-width: 991px) {
              margin: 0 0 5px;
            }
          }

          .selected-lab-placeholder {
            color: variables.$grey-5;
            border: 1px solid variables.$grey-5;
            border-radius: 4px;
            padding: 5px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .selected-lab {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 5px;
            font-weight: bold;
            border-radius: 4px;
            position: relative;
            overflow: hidden;
            min-height: 38px;

            .shop-name {
              text-overflow: ellipsis;
              overflow: hidden;
              white-space: nowrap;
              width: 70%;
              padding: 0 5px;
            }

            .fonticon-close {
              cursor: pointer;
            }

            .icon-container {
              width: 32px !important;
              height: 32px !important;
              line-height: 32px;
              color: white;

              .logo-initials {
                width: 100%;
                height: 100%;
                font-size: 8px;
              }

              img {
                height: inherit;
                width: inherit;
              }
            }
          }
        }

        .buttons-zone {
          button {
            height: 38px;
          }
        }
      }

      .results-map-sort {
        display: flex;
        flex-direction: row;
        text-align: left;
        margin: 8px 18px;
      }

      .map-icon {
        margin-right: 10px;
        color: variables.$grey-6;
        font-size: 16px;
        transform: translateY(-3px);
        cursor: pointer;
      }

      .fonticon-map {
        &:hover {
          color: variables.$grey-7;
        }
      }
    }

    .fonticon.active {
      color: variables.$blue-3;
    }

    .filters-line {
      border: 1px solid variables.$grey-4;
      border-top: 0px;
      border-radius: 0px 0px 5px 5px;
      display: flex;
      flex-direction: column;
      background-color: variables.$grey-2;
      margin: 0px auto;
      padding: 4px 15px;

      .autocomplete-searchbox .input::placeholder {
        font-style: italic;
        color: variables.$grey-7;
        opacity: 0.5;
      }

      .form-group {
        width: 100%;
        margin-bottom: 0px !important;

        .form-control {
          margin: 0;
        }

        .select {
          flex-grow: 1;
          margin: 0;
        }

        .autocomplete-searchbox {
          margin: 0;
        }
      }

      .filters-container {
        .group-filter-and-label {
          @media screen and (min-width: variables.$screen-sm-max) {
            display: flex;
            align-items: center;
          }

          .filter-label {
            white-space: nowrap;
            margin-right: 5px;
          }
        }
      }
    }

    .expand-sort {
      cursor: pointer;

      .fonticon-down-dir {
        transition: transform 0.3s ease;
        position: relative;
        bottom: 2px;
        font-size: 9px;
        color: variables.$grey-6;
      }

      &:hover {
        .fonticon-down-dir {
          color: variables.$grey-7;
        }
      }

      &.filters-collapsed {
        .fonticon-down-dir {
          transform: rotateZ(-90deg);
        }

        .emp-lab-list-message {
          width: 100%;
        }
      }
    }
  }
}
</style>
